// Scheduled tasks — durable nudge delivery (ENG-7971).
//
// enqueueScheduledTaskNotice wraps POST /host/scheduled-task/notify, which
// enqueues the "work this scheduled-task card" nudge as a direct_chat_messages
// notice (kind:'notice') on the verified direct-chat rail instead of firing it
// over unverified tmux send-keys (which always returned delivered=false
// post-ENG-6345 and could orphan the card in 'todo' with no recovery -
// ENG-6351). Best-effort — never throws to the caller; a fault logs and reports
// not-ok so the caller can close the card/run rather than leave it stranded.

import { createHash } from 'node:crypto';
import { api } from '../../api-client.js';
import { log } from '../runtime.js';

export interface EnqueueScheduledTaskNoticeOptions {
  agentId: string;
  /** The scheduled task id (source_ref of the card); the notice session key. */
  taskId: string;
  /** The materialised kanban card id the nudge tells the agent to work. */
  cardId: string;
  /** The full nudge text (task framing + run marker). */
  content: string;
  /** Attribution run id, carried in the notice payload (best-effort). */
  runId?: string | null;
  /**
   * ENG-8295: the task's human name, carried in the payload so the API can name
   * the task in the one-line summary shown to a USER in place of `content`
   * (which is written to the agent and reads as an order to the reader).
   *
   * Display-only: nothing the agent receives depends on it. An omitted name
   * costs the summary its specificity ("A scheduled task started.") and nothing
   * more, which is also what every row written before ENG-8295 renders as.
   */
  taskName?: string | null;
}

/**
 * Enqueue the scheduled-task nudge onto the direct-chat durable rail.
 *
 * Returns `true` when the rail accepted the nudge — both a fresh enqueue and an
 * `already_pending` skip count as success (the durable queue still holds an
 * outstanding notice for this task). Returns `false` only on a transport/route
 * error, signalling the caller to close the card + run rather than leave it
 * orphaned (there is no send-keys fallback any more).
 */
export async function enqueueScheduledTaskNotice(
  opts: EnqueueScheduledTaskNoticeOptions,
): Promise<boolean> {
  try {
    const res = await api.post<{ ok?: boolean; enqueued?: boolean; reason?: string }>(
      '/host/scheduled-task/notify',
      {
        agent_id: opts.agentId,
        task_id: opts.taskId,
        card_id: opts.cardId,
        content: opts.content,
        run_id: opts.runId ?? undefined,
        task_name: opts.taskName ?? undefined,
      },
    );
    return res.ok === true;
  } catch (err) {
    // Hash the raw error text so durable manager logs don't carry potentially
    // sensitive payloads (per CLAUDE.md log policy).
    const errText = err instanceof Error ? err.message : String(err);
    const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
    log(
      `[scheduled-kanban] notice enqueue failed for agent_id=${opts.agentId} task_id=${opts.taskId} error_id=${errId}`,
    );
    return false;
  }
}
